import { ensureDeviceObservation } from '@/lib/deviceFingerprint';
import React, { useRef, useCallback, useReducer } from 'react';
import { StyleSheet, View, Keyboard, Platform } from 'react-native';
import { router } from '@/lib/navigation';
import PropTypes from 'prop-types';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import FloatingLabelInput from '@/components/Inputs/FloatingLabelInput';
import { API_HOST } from '@/constants/config';
import { useAuthStore } from '@/store';
import Turnstile from '@/components/Turnstile';
import SubmitButton from '@/components/Buttons/SubmitButton';
import Link from '@/components/Typography/Link';
import StatusContainer from '@/components/StatusContainer';
import SSOButtons from '@/components/Auth/SSOButtons';
import { pendingReferral } from '@/lib/pendingReferral';
import { replaceAfterWebSignIn } from '@/lib/webSignInNavigation';
import { getClientInfo, trackUser, trackWhopCompleteRegistration } from '@/constants/utils/analytics';
import { trackAuthEvent, AUTH_ACTION } from '@/constants/utils/analyticsEvents';

const styles = StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: 400,
    gap: 6,
  },
  inputContainer: {
    gap: 8,
  },
  footer: {
    gap: 12,
    marginTop: 24,
    alignItems: 'center',
  },
  footerLinks: {
    flexDirection: 'row',
    gap: 4,
    justifyContent: 'center',
  },
});

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Platform check
const isWeb = Platform.OS === 'web';

// Form validation
const validateEmail = (email, t) => {
  if (!email.trim()) {
    return t('auth.signup.errors.emailRequired');
  }
  if (!EMAIL_REGEX.test(email.trim())) {
    return t('auth.signup.errors.invalidEmail');
  }
  return null;
};

const validatePassword = (password, t) => {
  if (!password.trim()) {
    return t('auth.signup.errors.passwordRequired');
  }
  if (password.length < 8) {
    return t('auth.signup.errors.passwordTooShort');
  }
  return null;
};

// Form reducer
const formReducer = (state, action) => {
  switch (action.type) {
    case 'SET_EMAIL':
      return { ...state, email: action.payload, error: '' };
    case 'SET_PASSWORD':
      return { ...state, password: action.payload, error: '' };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_VERIFYING':
      return { ...state, isVerifying: action.payload };
    case 'RESET':
      return { ...initialFormState };
    default:
      return state;
  }
};

// Initial form state
const initialFormState = {
  email: '',
  password: '',
  error: '',
  isLoading: false,
  isVerifying: false,
};

// Memoized footer links component
const FormLinks = React.memo(({ t }) => (
  <View style={styles.footer}>
    <View style={styles.footerLinks}>
      <Text color='secondary'>{t('auth.signup.haveAccount')}</Text>
      <Link href="/login">
        {t('auth.signup.loginLink')}
      </Link>
    </View>
  </View>
));

FormLinks.propTypes = {
  t: PropTypes.func.isRequired,
};

const SignupForm = ({ onSuccess, replaceUrl = isWeb ? '/app' : '/', showLinks = true, redirectAfterSuccess = true }) => {
  const { t } = useTranslation();
  const setAuthData = useAuthStore(state => state.setAuthData);
  const turnstileRef = useRef(null);
  const passwordInputRef = useRef(null);
  const submitting = useRef(false);
  
  const [formState, dispatch] = useReducer(formReducer, initialFormState);

  // Memoized input handlers
  const handleEmailChange = useCallback((text) => {
    dispatch({ type: 'SET_EMAIL', payload: text });
  }, []);

  const handlePasswordChange = useCallback((text) => {
    dispatch({ type: 'SET_PASSWORD', payload: text });
  }, []);

  // Validate form
  const validateForm = useCallback(() => {
    const emailError = validateEmail(formState.email, t);
    if (emailError) {
      dispatch({ type: 'SET_ERROR', payload: emailError });
      return false;
    }

    const passwordError = validatePassword(formState.password, t);
    if (passwordError) {
      dispatch({ type: 'SET_ERROR', payload: passwordError });
      return false;
    }

    return true;
  }, [formState.email, formState.password, t]);

  const handleSignup = useCallback(async () => {
    if (submitting.current) return;
    submitting.current = true;
    try {
      Keyboard.dismiss();
      if (!validateForm()) return;

      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: '' });

      // Get turnstile token on submit
      let turnstileToken = null;
      if (isWeb) {
        dispatch({ type: 'SET_VERIFYING', payload: true });
        try {
          turnstileToken = await turnstileRef.current?.execute();
          if (typeof turnstileToken !== 'string' || !turnstileToken.trim()) {
            throw new Error('Browser security check returned no token');
          }
        } catch (captchaError) {
          const messageKey = captchaError.code === 'script_unavailable'
            ? 'browserCheckUnavailable'
            : captchaError.code === 'timeout' ? 'browserCheckTimedOut' : 'browserCheckFailed';
          dispatch({ type: 'SET_ERROR', payload: t(`auth.signup.errors.${messageKey}`) });
          return;
        } finally {
          dispatch({ type: 'SET_VERIFYING', payload: false });
        }
      }

      await ensureDeviceObservation();
      const clientInfo = getClientInfo();

      if (__DEV__ && !isWeb) {
        console.log('[Signup] API_HOST:', API_HOST);
      }

      const referralCode = await pendingReferral.read();

      const response = await fetch(`${API_HOST}/json/auth/signup/email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Match local Debug login: native fetch does not send Origin.
          ...(__DEV__ && !isWeb && /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(API_HOST)
            ? { Origin: API_HOST }
            : {}),
        },
        body: JSON.stringify({ 
          email: formState.email.trim(), 
          password: formState.password.trim(),
          ...(isWeb && { turnstileToken }),
          ...(referralCode && { referralCode }),
          clientInfo,
        }),
      });

      if (__DEV__ && !isWeb) {
        console.log('[Signup] Response status:', response.status);
      }

      let data;
      try {
        data = await response.json();
      } catch (_) {
        throw new Error(t('auth.signup.errors.signupFailed'));
      }

      if (!response.ok || data.error) {
        const errorMsg = isWeb && data.message === 'Invalid captcha response'
          ? t('auth.signup.errors.browserCheckFailed')
          : data.message || t('auth.signup.errors.signupFailed');
        throw new Error(errorMsg);
      }

      if (data.tokens?.access) {
        await setAuthData(data.tokens.access, data.tokens.expiresAt, data.tokens.refresh, data.user);
      }

      await pendingReferral.clear(referralCode);

      if (data.user?.id) trackUser(data.user.id);
      trackAuthEvent(AUTH_ACTION.SIGNUP_SUCCESS, 'email');
      trackWhopCompleteRegistration();
      onSuccess?.(data);
      if (redirectAfterSuccess && replaceUrl) {
        if (isWeb) replaceAfterWebSignIn(router, replaceUrl);
        else router.replace(replaceUrl);
      }
    } catch (error) {
      trackAuthEvent(AUTH_ACTION.SIGNUP_FAILED, 'email');
      dispatch({ type: 'SET_ERROR', payload: error.message || t('auth.signup.errors.signupFailed') });
    } finally {
      submitting.current = false;
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [formState.email, formState.password, t, onSuccess, setAuthData, validateForm, replaceUrl, redirectAfterSuccess]);

  return (
    <View style={styles.container}>
      <SSOButtons onSuccess={onSuccess} redirectAfterSuccess={redirectAfterSuccess} mode="signup" />

      <View style={styles.inputContainer}>
        <FloatingLabelInput
          label={t('auth.signup.emailLabel')}
          value={formState.email}
          onChangeText={handleEmailChange}
          keyboardType="email-address"
          autoCapitalize="none"
          onSubmitEditing={() => passwordInputRef.current?.focus()}
          returnKeyType="next"
          blurOnSubmit={false}
          autoComplete="email"
          textContentType="emailAddress"
        />

        <FloatingLabelInput
          ref={passwordInputRef}
          label={t('auth.signup.passwordLabel')}
          value={formState.password}
          onChangeText={handlePasswordChange}
          secureTextEntry
          onSubmitEditing={handleSignup}
          returnKeyType="go"
          autoComplete="new-password"
          textContentType="newPassword"
        />
      </View>

      {formState.error ? (
        <StatusContainer type="error" message={formState.error} />
      ) : null}

      {isWeb && <Turnstile ref={turnstileRef} />}

      {formState.isVerifying ? (
        <Text color="secondary" accessibilityLiveRegion="polite">
          {t('auth.signup.checkingBrowser')}
        </Text>
      ) : null}

      <SubmitButton
        onPress={handleSignup}
        isLoading={formState.isLoading}
        isDisabled={!formState.email.trim() || !formState.password.trim()}
        title={t('auth.signup.submitButton')}
      />

      {showLinks && <FormLinks t={t} />}
    </View>
  );
};

SignupForm.propTypes = {
  onSuccess: PropTypes.func,
  redirectAfterSuccess: PropTypes.bool,
  showLinks: PropTypes.bool,
};

export default React.memo(SignupForm);
